import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { useAuth } from '@/hooks/auth/useAuth';
import { toast } from 'sonner';
import type { AdminAgentModelEntry } from '@reflex/shared';

type OrgPolicyScope = 'org';

export function useAgentModelPolicy(options: { enabled?: boolean } = {}) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: queryKeys.agentModelPolicy(orgKey),
    queryFn: api.getAgentModelPolicy,
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

export function useAgentModelPolicyEntry(agentType: string): AdminAgentModelEntry | null {
  const { activeOrganizationId } = useAuth();
  const { data } = useAgentModelPolicy({ enabled: Boolean(activeOrganizationId) });
  return data?.agents.find((e: AdminAgentModelEntry) => e.agentType === agentType) ?? null;
}

export function useToggleAgentProvider() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentType,
      providerId,
      enabled,
      scope,
    }: {
      agentType: string;
      providerId: string;
      enabled: boolean;
      scope: OrgPolicyScope;
    }) => api.toggleAgentProvider(agentType, providerId, enabled, scope),
    onSuccess: () => {
      // Invalidate every org's cached copy via prefix match because the active
      // organization can change while sibling caches remain mounted.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'agent-model-policy'] });
      void queryClient.invalidateQueries({ queryKey: ['config', 'agent-model-support'] });
      toast.success('Provider setting updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update provider', { description: error.message });
    },
  });
}

export function useToggleAgentModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentType,
      modelId,
      enabled,
      scope,
    }: {
      agentType: string;
      modelId: string;
      enabled: boolean;
      scope: OrgPolicyScope;
    }) => api.toggleAgentModel(agentType, modelId, enabled, scope),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'agent-model-policy'] });
      void queryClient.invalidateQueries({ queryKey: ['config', 'agent-model-support'] });
      toast.success('Model setting updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update model', { description: error.message });
    },
  });
}

export function useSetAgentDefault() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentType,
      defaults,
      scope,
    }: {
      agentType: string;
      defaults: { modelId?: string; providerId?: string };
      scope: OrgPolicyScope;
    }) => api.setAgentModelDefault(agentType, defaults, scope),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'agent-model-policy'] });
      void queryClient.invalidateQueries({ queryKey: ['config', 'agent-model-support'] });
      toast.success('Default updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update default', { description: error.message });
    },
  });
}

export function useSetLaunchDefaultAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { scope: OrgPolicyScope; agentType?: string }) =>
      api.setLaunchDefaultAgent(params),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'agent-model-policy'] });
      void queryClient.invalidateQueries({ queryKey: ['config', 'agent-model-support'] });
      toast.success('Launch default updated');
    },
    onError: (error: Error) => {
      toast.error('Failed to update launch default', { description: error.message });
    },
  });
}

/**
 * Toggle the agent-level enable/disable state (PRD-24). When disabled, the
 * agent type is hidden from the Launch dialog selector and rejected by the
 * server if a launch request is submitted.
 */
export function useSetAgentTypeEnabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { agentType: string; enabled: boolean; scope: OrgPolicyScope }) =>
      api.setAgentTypeEnabled(params.agentType, params.enabled, params.scope),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'agent-model-policy'] });
      void queryClient.invalidateQueries({ queryKey: ['config', 'agent-model-support'] });
      toast.success(variables.enabled ? 'Agent enabled' : 'Agent disabled');
    },
    onError: (error: Error) => {
      toast.error('Failed to update agent availability', { description: error.message });
    },
  });
}
